import React from 'react';
import {Pressable, StyleSheet, TextStyle, useWindowDimensions, View, ViewStyle} from 'react-native';
import {Text, TextInput, Button} from 'ui-kits';
import {Formik, FormikProps} from 'formik';
import {useRequiredFields} from 'hooks';
import {RegisterOrganizationValidation} from 'utils/schema';
import {registerOrganizationRequestPayload} from 'redux/slices/authSlice';
import colors from 'theme/Colors';
import {spacing, corner_radius} from 'theme/Units';
import { useResponsiveStyles } from 'hooks/useResponsiveStyles';
import { mobileViewportWidth } from 'constants/DefaultValues';

type propType = Readonly<{
  initialValue: registerOrganizationRequestPayload;
  onSubmit: (values: registerOrganizationRequestPayload) => void;
  navigateToHome: () => void;
}>;
export function RegisterOrginizationForm(props: propType) {
  const {initialValue, onSubmit, navigateToHome} = props;
  return (
    <Formik
      initialValues={initialValue}
      validationSchema={RegisterOrganizationValidation}
      onSubmit={onSubmit}>
      {formikProps => {
        return <OrganizationRegistrationForm navigateToHome={navigateToHome} {...formikProps} />;
      }}
    </Formik>
  );
}

type OrganizationRegistrationPropType = {navigateToHome: () => void} & FormikProps<registerOrganizationRequestPayload>;
function OrganizationRegistrationForm(props: OrganizationRegistrationPropType) {
  const {
    handleChange,
    handleBlur,
    handleSubmit,
    values,
    errors,
    dirty,
    isValid,
    navigateToHome,
  } = props;

  const reuiredField = useRequiredFields(RegisterOrganizationValidation);
  const styles = useResponsiveStyles(baseStyles, responsive);

  let size = 'md';
  const {width} = useWindowDimensions();
  if (width < mobileViewportWidth){
    size = 'sm';
  }
  return (
    <View style={styles.form}>
      <Text variant="inter18" style={styles.heading}>
        Register
      </Text>
      <TextInput
        label="Organization Name"
        size={size}
        value={values.org_name}
        error={errors.org_name}
        onChangeText={handleChange('org_name')}
        onBlur={handleBlur('org_name')}
        placeholder="Enter your organization name"
        required={reuiredField.includes('org_name')}
      />
      <View style={styles.nameFielsWrapper}>
        <TextInput
          label="First Name"
          size={size}
          value={values.first_name}
          error={errors.first_name}
          onChangeText={handleChange('first_name')}
          onBlur={handleBlur('first_name')}
          placeholder="Enter your first name"
          maxLength={15}
          required={reuiredField.includes('first_name')}
        />
        <TextInput
          label="Last Name"
          size={size}
          value={values.last_name}
          error={errors.last_name}
          onChangeText={handleChange('last_name')}
          onBlur={handleBlur('last_name')}
          placeholder="Enter your last name"
          maxLength={15}
          required={reuiredField.includes('last_name')}
        />
      </View>

      <TextInput
        label="Email ID"
        size={size}
        value={values.email}
        error={errors.email}
        onChangeText={handleChange('email')}
        onBlur={handleBlur('email')}
        placeholder="Enter your email address"
        required={reuiredField.includes('email')}
      />
      <Button
        style={styles.submitButton}
        text="Start Your 1IDP Trial"
        disabled={!isValid || !dirty}
        onPress={handleSubmit}
      />
      <Pressable onPress={navigateToHome} style={styles.homeButton}>
        <Text style={styles.homeLink}>Return to Home</Text>
      </Pressable>
    </View>
  );
}

type styleType = {
  form: ViewStyle;
  heading: TextStyle;
  nameFielsWrapper: ViewStyle;
  submitButton: ViewStyle;
  homeButton: ViewStyle;
  homeLink: TextStyle;
};
const baseStyles: styleType = StyleSheet.create({
  homeLink: {
    textAlign: 'center',
    color: colors.palette.blueInfo,
  },
  homeButton: {
    paddingHorizontal: spacing['4'],
    paddingVertical: spacing['2'],
  },
  form: {
    gap: spacing['4'],
    padding: spacing['4'],
    borderRadius: corner_radius['rounded-lg'],
    alignItems: 'center',
    minWidth: 0,
    width: '100%',
    maxWidth: 400,
    alignSelf: 'center',
    backgroundColor: colors.palette.white,
    shadowColor: 'rgb(23, 24, 28)',
    shadowOpacity: 0.1,
    shadowOffset: {height: 2, width: 0},
    shadowRadius: 4,
  },
  heading: {color: colors.palette.brandBlue, fontSize: 56, fontWeight: '600'},
  nameFielsWrapper: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: spacing['4'],
  },
  submitButton: {width: '100%'},
});

const responsive : Record<number, Partial<styleType>> = {
  640: {
    heading: {
      fontSize: 50,
    },
  },
  480: {
    heading: {
      fontSize: 44,
    },
  },
};
